/**
 * Summary stat-card header for a grid page.
 *
 * Renders the page's stat cards in the standard responsive StatCardGroup with
 * the standard page gap underneath — the block the grid pages each hand-wrote
 * around their card lists.
 *
 * A grid's cards are configurable per customer type and per user, so a page
 * can carry three or a dozen and the header has to read at either size. It
 * wraps onto as many rows as it needs, four across at desktop width, and the
 * cards spread evenly over those rows rather than leaving the last one nearly
 * empty (see resolveWrappedColumns). Below md the same cards are a carousel.
 */
import React from 'react';

import StatCard, { resolveWrappedColumns, StatCardGroup } from '@sophocles/components/StatCard';
import { TONE_TO_FAMILY } from '@sophocles/data-grids/grid/columnRenderers';

/**
 * Resolve a descriptor tone token to its status-palette CSS variable.
 * An unrecognized token reads neutral rather than emitting a var that does
 * not exist.
 *
 * @param {?string} tone
 * @returns {string|undefined}
 */
function toneColor(tone) {
  if (!tone) return undefined;
  return `var(--status-${TONE_TO_FAMILY[tone] ?? 'neutral'})`;
}

/**
 * @param {object} props
 * @param {Array<{ label: string, key?: string, tone?: string, color?: string,
 *   loading?: boolean, active?: boolean, onClick?: Function, tooltip?: string,
 *   display?: object }>} props.cards
 *   One entry per card. `key` identifies the card when present (two cards may
 *   legitimately carry the same label); `label` is the fallback key.
 *   A card supplies either a semantic `tone` — resolved here to the matching
 *   `--status-*` token — or a literal `color`, so a descriptor-driven card and
 *   a hand-written one can sit in the same row without one of them mixing MUI
 *   palette shorthand into a list of raw CSS vars.
 *   `display` holds the StatCard props that carry the numbers — `value` alone
 *   for a plain count, plus `change`/`previousValue` for a card measured
 *   against a comparison. What fills it is the card type's renderer's business
 *   (see resolveStatCardRenderer); this component only frames it.
 * @param {object} [props.sx] - Extra sx merged onto the group.
 */
export default function GridStatCards({ cards, sx }) {
  return (
    <StatCardGroup
      columns={resolveWrappedColumns(cards.length)}
      carouselBelow="md"
      sx={{ marginBottom: 'var(--page-gap)', ...sx }}
    >
      {cards.map(card => (
        <StatCard
          key={card.key ?? card.label}
          title={card.label}
          color={card.color ?? toneColor(card.tone)}
          loading={card.loading}
          tooltip={card.tooltip}
          active={card.active}
          onClick={card.onClick}
          {...card.display}
        />
      ))}
    </StatCardGroup>
  );
}
